import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MetadataService } from '@app/finxone-web-frontend/app/lib/services/metadata-service/metadata-service.service';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { findAndShowAlertFromCode } from '@app/finxone-web-frontend/app/lib/utils/alert-code-utils/alert-code.utils';
import { EmailVerifyErrorCodes, PhoneNumberVerifyErrorCodes } from '@app/translations';
import { FormID, OtpFlowEnum, OtpParam } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  BaseWidgetProperties,
  ProfileStateModel,
  UserProfileData,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { NgOtpInputComponent } from 'ng-otp-input';
import { Subject, catchError, finalize, first, map, take, takeUntil, throwError } from 'rxjs';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-base-otp-verification',
  templateUrl: './base-otp-verification.component.html',
  styleUrls: ['./base-otp-verification.component.scss'],
})
export class BaseOtpVerificationComponent implements OnInit, AfterViewInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  @ViewChild('ngOtpInput', { static: false }) ngOtpInput: NgOtpInputComponent;
  @Input() public widgetConfig: BaseWidgetProperties;
  @Input() public userProfileData: UserProfileData;
  @Input() public contentStyle: baseCssConfigStyle;
  @Input() public inputStyle: baseCssConfigStyle;
  @Input() public textContentStyle: baseCssConfigStyle;
  @Input() public inputBaseStyle: baseCssConfigStyle;
  @Input() public inputInactiveStyle: baseCssConfigStyle;
  @Input() public colorStyle: baseCssConfigStyle;
  @Input() public verifyButtonStyle: baseCssConfigStyle;
  @Input() public resendButtonStyle: baseCssConfigStyle;
  @Input() public buttonContent: string;
  @Input() public resendButtonContent: string;
  @Input() public fieldValue: string;
  @Input() public timerTextStyle: baseCssConfigStyle;
  @Input() public timerTextContent: string;
  @Input() public maxLength: number;
  @Output() otpSubmitAndProcess = new EventEmitter<boolean>();
  @Input() buttonGlobalStyles = {
    verifyButton: {
      globalStylingClass: '',
      isAllowedGlobalStyling: false,
    },
    resendButton: {
      globalStylingClass: '',
      isAllowedGlobalStyling: false,
    },
  };
  @Output() otpSubmissionHandler = new EventEmitter<{
    otpParam: string;
    code: string;
  }>();

  public fieldType: 'email' | 'phone';
  public currentStyle: baseCssConfigStyle;
  public code = '';
  public isTimerSeparated: boolean;
  public isDisableResendBtn = false;
  /**
   * True while a verify-otp-common request for the currently-entered code is in flight. The
   * "Verify" button was only ever disabled by code length, so a double-click/double-tap fired two
   * identical requests; some providers still report an already-approved code as approved on a
   * second check, so both requests would succeed server-side, each minting its own random
   * verificationToken and overwriting the same Redis entry - whichever response the browser store
   * saw last could then disagree with whichever write the server processed last. Guarding
   * re-entrancy here keeps this to a single verify per submitted code.
   */
  public isVerifying = false;
  public timer = 60;
  private intervalId: any;
  private flutterWebView = (window as any).flutter_inappwebview;
  profileData: ProfileStateModel;
  formActionState: any;
  @ViewChild('otpInput', { static: false }) otpInput!: ElementRef;

  constructor(
    private metaService: MetadataService,
    public pageConfig: PageConfigurationProvider,
    private renderer: Renderer2,
    private route: ActivatedRoute,
    private store: Store,
    private alertHandlerService: AlertHandlerService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.flutterWebView) {
      this.flutterWebView.callHandler('otpState', ['started']);
    }
    this.fieldType = this.widgetConfig.widgetContext.type;
    this.isTimerSeparated = this.widgetConfig.inputFieldSettings?.isTimerSeparated as boolean;
    this.maxLength = this.fieldType === 'email' ? 4 : 6;

    this.isDisableResendBtn = true;
    this.sendOtp();
    this.listenForOtpAutocomplete();
    this.currentStyle = Object.assign(this.inputBaseStyle, this.inputInactiveStyle);
  }

  public listenForOtpAutocomplete() {
    window.addEventListener(
      'phoneOtpMessage',
      (event: any) => {
        this.code = event.detail;
      },
      false,
    );
  }

  public onOtpChange(otp: string) {
    this.code = otp;
  }

  public verifyOTP() {
    // Re-entrancy guard: without this, a double-click/double-tap before the first request
    // resolves fires a second verify-otp-common call for the same code, which can produce a
    // second, different verificationToken racing the first to overwrite the same Redis entry.
    if (this.isVerifying) {
      return;
    }
    this.isVerifying = true;

    const userId = this.store.selectSnapshot(ProfileState.getProfile)?.id;
    const payload = {
      fieldType: this.fieldType,
      fieldValue: {
        [this.fieldType]: this.fieldValue,
      },
      verificationCode: this.code,
      userId: userId,
      formId: this.resolveOtpFormId(),
    };

    // take(1): this subscribes to route.queryParams afresh on every call, and previously never
    // unsubscribed until component destroy - queryParams re-emitting later (e.g. from an
    // unrelated navigation) would silently re-run the verify call for whichever past code was
    // closured into that invocation's payload.
    this.route?.queryParams.pipe(take(1), takeUntil(this.destroy$)).subscribe((params) => {
      const otpParam = params['otp'];
      const specificOtpParams = [OtpParam.PaymentRequest];
      if (specificOtpParams.includes(otpParam)) {
        this.metaService
          .verifyOtpPhoneEmail(payload)
          .pipe(
            takeUntil(this.destroy$),
            map(() => {
              if (this.flutterWebView) {
                this.flutterWebView.callHandler('otpState', ['completed']);
              }
              window.removeEventListener('phoneOtpMessage', this.reset);
              this.handleOtpAlert('success');
            }),
            catchError((error) => {
              this.ngOtpInput.setValue('');
              this.handleOtpAlert('error');
              return throwError(() => error);
            }),
            finalize(() => {
              this.isVerifying = false;
            }),
          )
          .subscribe();
      } else {
        this.metaService
          .verifyOtpPhoneEmail(payload)
          .pipe(
            takeUntil(this.destroy$),
            map((res) => {
              if (this.flutterWebView) {
                this.flutterWebView.callHandler('otpState', ['completed']);
              }
              window.removeEventListener('phoneOtpMessage', this.reset);
              this.otpSubmitAndProcess.emit(res);
              this.handleOtpAlert('success');
            }),
            catchError((error) => {
              this.ngOtpInput.setValue('');
              this.handleOtpAlert('error');
              return throwError(() => error);
            }),
            finalize(() => {
              this.isVerifying = false;
            }),
          )
          .subscribe();
      }
    });
  }

  private resolveOtpFormId(): string | undefined {
    switch (this.widgetConfig.widgetContext.flow) {
      case OtpFlowEnum.ONBOARDING_V2:
        return FormID.ONBOARDING_FLOW;
      case OtpFlowEnum.BUSINESS_ONBOARDING:
        return FormID.BUSINESS_ONBOARDING_FLOW;
      default:
        return undefined;
    }
  }

  private handleOtpAlert(type: 'success' | 'error') {
    const alertMap = {
      phone: {
        success: PhoneNumberVerifyErrorCodes.PHONE_VERIFIED,
        error: PhoneNumberVerifyErrorCodes.WRONG_PHONE_OTP,
      },
      email: {
        success: EmailVerifyErrorCodes.EMAIL_VERIFIED,
        error: EmailVerifyErrorCodes.WRONG_EMAIL_OTP,
      },
    };

    const code = alertMap[this.fieldType]?.[type];

    if (code) {
      findAndShowAlertFromCode(this.store, this.router, this.alertHandlerService, [code]);
    }
  }

  private reset() {
    this.code = '';
  }

  public sendOtp() {
    this.code = '';
    const payload = {
      fieldType: this.fieldType,
      fieldValue: {
        [this.fieldType]: this.fieldValue,
      },
    };
    this.isDisableResendBtn = true;
    this.clearTimer();
    this.metaService
      .generateOtpPhoneEmail(payload)
      .pipe(first())
      .subscribe({
        next: () => {
          this.startCountdown();
        },
        error: () => {
          this.clearTimer();
        },
      });
  }

  public setCurrentStyles(event: any, isFocused: boolean) {
    this.currentStyle = isFocused
      ? { ...this.inputBaseStyle, ...this.inputStyle }
      : { ...this.inputBaseStyle, ...this.inputInactiveStyle };
  }

  private startCountdown(): void {
    this.timer = 60; // Reset timer to 60 seconds
    this.isDisableResendBtn = true;
    this.intervalId = setInterval(() => {
      if (this.timer > 0) {
        this.timer--;
      } else {
        this.clearTimer();
      }
    }, 1000);
  }

  private clearTimer(): void {
    this.isDisableResendBtn = false;
    clearInterval(this.intervalId);
  }

  private formatTimer(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }

  get formattedTimer(): string {
    return this.formatTimer(this.timer);
  }

  get resendTextContent() {
    return this.timer && !this.isTimerSeparated
      ? this.resendButtonContent + ' ' + this.formattedTimer
      : this.resendButtonContent;
  }

  ngAfterViewInit() {
    const inputElements = this.otpInput?.nativeElement.querySelectorAll('input');
    inputElements.forEach((inputElement: HTMLInputElement) => {
      this.renderer.addClass(inputElement, 'inputField');
    });
  }
}
